import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  ImagePlus,
  LoaderCircle,
  RefreshCw,
  Save,
  Trash2,
  UserCircle2,
} from 'lucide-react';

import { getUser, updateAccount } from '../Services/UserService';
import { useAuth } from '@/Modules/Core/Context/AuthContext';
import { Button } from '@/Modules/Core/Components/shadcn/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/Modules/Core/Components/shadcn/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/Modules/Core/Components/shadcn/dialog';
import { Input } from '@/Modules/Core/Components/shadcn/input';
import { Label } from '@/Modules/Core/Components/shadcn/label';
import { AppPageSection } from '@/Modules/Core/Components/Layout/AppPage';
import { cn } from '@/lib/utils';

const INITIAL_FORM_DATA = {
  email: '',
  name: '',
  profile_image: '',
};

const isSuccessfulResponse = (response) =>
  response?.success === true ||
  response?.isSuccess === true ||
  response?.statusCode?.code === 0;

const getResponseMessage = (response, fallbackMessage) =>
  response?.message ||
  response?.statusText ||
  response?.statusCode?.description ||
  fallbackMessage;

const normalizeAccountPayload = (response) => {
  const payload = response?.payload ?? response?.data ?? response ?? {};
  const detail = payload?.detail ?? payload?.user_detail ?? payload?.user_meta ?? {};

  return {
    email: payload?.email_address || payload?.email || '',
    name:
      payload?.username ||
      payload?.name ||
      payload?.full_name ||
      detail?.username ||
      '',
    profile_image:
      detail?.profile_image ||
      detail?.profileImage ||
      payload?.profile_image ||
      '',
  };
};

const AccountPageFallback = ({ error, resetErrorBoundary }) => (
  <div className="tw">
    <div className="space-y-6">
      <AppPageSection>
        <Card className="border-destructive/20 bg-card">
          <CardContent className="px-6 py-8">
            <div className="mx-auto max-w-2xl space-y-5 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h2 className="text-[24px] font-semibold text-foreground">
                  We couldn&apos;t load your account page
                </h2>
                <p className="text-[14px] leading-6 text-muted-foreground">
                  Something unexpected happened while rendering your profile
                  details. Please try again.
                </p>
              </div>
              {error?.message ? (
                <div className="rounded-2xl border border-destructive/15 bg-destructive/6 px-4 py-3 text-left text-[13px] leading-6 text-foreground">
                  {error.message}
                </div>
              ) : null}
              <div className="flex justify-center">
                <Button type="button" onClick={resetErrorBoundary}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try again
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </AppPageSection>
    </div>
  </div>
);

const Account = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [imageURL, setImageURL] = useState(null);
  const [isImageChanged, setIsImageChanged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [feedbackDialog, setFeedbackDialog] = useState({
    open: false,
    tone: 'success',
    title: '',
    message: '',
  });
  const fileInputRef = useRef(null);

  const showFeedbackDialog = useCallback((tone, title, message) => {
    setFeedbackDialog({
      open: true,
      tone,
      title,
      message,
    });
  }, []);

  const loadAccount = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');

    try {
      const response = await getUser();

      if (!isSuccessfulResponse(response)) {
        throw new Error(
          getResponseMessage(response, 'Unable to load your account details.')
        );
      }

      const normalizedPayload = normalizeAccountPayload(response);

      setFormData(normalizedPayload);
      setImageLoadFailed(false);
      setImageURL(normalizedPayload.profile_image || null);
    } catch (error) {
      const message =
        error?.message || 'Unable to load your account details right now.';
      setLoadError(message);
      setFormData(INITIAL_FORM_DATA);
      setImageURL(null);
      setImageLoadFailed(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      showFeedbackDialog(
        'error',
        'Invalid image format',
        'Please choose a JPG or PNG image for your profile thumbnail.'
      );
      event.target.value = '';
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = () => {
      setImageLoadFailed(false);
      setImageURL(fileReader.result);
      setFormData((prevData) => ({
        ...prevData,
        profile_image: fileReader.result,
      }));
      setIsImageChanged(true);
      event.target.value = '';
    };
    fileReader.readAsDataURL(file);
  };

  const handleImageError = () => {
    setImageLoadFailed(true);
  };

  const handleOpenImagePicker = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setImageURL(null);
    setImageLoadFailed(false);
    setIsImageChanged(true);
    setFormData((prevData) => ({
      ...prevData,
      profile_image: '',
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setIsSaving(true);

      const dataToSubmit = {
        ...formData,
        userImage: isImageChanged ? imageURL : formData.profile_image,
      };

      try {
        const response = await updateAccount(dataToSubmit);

        if (isSuccessfulResponse(response)) {
          const nextProfileImage = dataToSubmit.userImage || null;

          showFeedbackDialog(
            'success',
            'Successfully saved',
            'Your profile changes have been saved successfully.'
          );
          setImageURL(nextProfileImage);
          setImageLoadFailed(false);
          setFormData((currentData) => ({
            ...currentData,
            profile_image: nextProfileImage || currentData.profile_image,
          }));
          setIsImageChanged(false);
          updateUser({
            ...(user || {}),
            username: formData.name || user?.username || user?.userName,
            userName: formData.name || user?.userName || user?.username,
            email: formData.email || user?.email,
            email_address: formData.email || user?.email_address || user?.email,
            profile_image: nextProfileImage,
            detail: {
              ...(user?.detail || {}),
              profile_image: nextProfileImage,
            },
          });
        } else {
          showFeedbackDialog(
            'error',
            'Failed to submit form',
            getResponseMessage(response, 'Unable to save your profile changes.')
          );
        }
      } catch (error) {
        const message =
          error?.message || 'Unable to save your profile changes right now.';
        console.error('Error submitting form:', error);
        showFeedbackDialog('error', 'Error submitting form', message);
      } finally {
        setIsSaving(false);
      }
    },
    [formData, imageURL, isImageChanged, showFeedbackDialog, updateUser, user]
  );

  const previewUnavailable = !imageURL || imageLoadFailed;

  return (
    <ErrorBoundary
      FallbackComponent={AccountPageFallback}
      onReset={() => {
        loadAccount();
      }}
    >
      <div className="tw">
        <div className="space-y-6">
          <AppPageSection>
            {isLoading ? (
              <Card className="account-profile-shell">
                <CardContent className="px-5 py-8">
                  <div className="flex min-h-[280px] flex-col items-center justify-center gap-4 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <LoaderCircle className="h-6 w-6 animate-spin" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-[18px] font-semibold text-foreground">
                        Loading your account
                      </p>
                      <p className="max-w-md text-[13px] leading-6 text-muted-foreground">
                        Pulling in your profile details and saved preferences.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : loadError ? (
              <Card className="border-destructive/20 bg-card">
                <CardContent className="px-6 py-8">
                  <div className="mx-auto max-w-2xl space-y-5 text-center">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
                      <AlertTriangle className="h-6 w-6" />
                    </div>
                    <div className="space-y-2">
                      <h2 className="text-[24px] font-semibold text-foreground">
                        Your account details couldn&apos;t be loaded
                      </h2>
                      <p className="text-[14px] leading-6 text-muted-foreground">
                        We couldn&apos;t fetch your profile information from the
                        server. You can retry now, and if the issue continues,
                        check the account API response.
                      </p>
                    </div>
                    <div className="rounded-2xl border border-destructive/15 bg-destructive/6 px-4 py-3 text-left text-[13px] leading-6 text-foreground">
                      {loadError}
                    </div>
                    <div className="flex justify-center">
                      <Button type="button" onClick={loadAccount}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Retry
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="account-profile-shell">
                <CardHeader className="border-b border-border/50 pb-5">
                  <CardTitle className="text-[20px] font-semibold text-foreground">
                    Profile details
                  </CardTitle>
                  <CardDescription className="mt-1 text-[13px] leading-6 text-muted-foreground">
                    Keep your profile and contact information up to date.
                  </CardDescription>
                </CardHeader>

                <CardContent className="px-5 py-5">
                  <form
                    id="account-profile-form"
                    className="account-profile-shell__form"
                    onSubmit={handleSubmit}
                  >
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="user_image">Profile Thumbnail</Label>
                      <input
                        ref={fileInputRef}
                        id="user_image"
                        name="user_image"
                        type="file"
                        accept=".jpg,.png,.jpeg,image/png,image/jpeg"
                        onChange={handleUpload}
                        className="sr-only"
                      />

                      <div className="flex flex-col gap-3 rounded-xl border border-border/60 bg-background px-3 py-3 sm:flex-row sm:items-center">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border/60 bg-muted/20">
                            {previewUnavailable ? (
                              <span
                                className={cn(
                                  'flex h-9 w-9 items-center justify-center rounded-lg',
                                  imageLoadFailed
                                    ? 'bg-destructive/10 text-destructive'
                                    : 'bg-primary/10 text-primary'
                                )}
                              >
                                {imageLoadFailed ? (
                                  <UserCircle2 className="h-5 w-5" />
                                ) : (
                                  <Camera className="h-5 w-5" />
                                )}
                              </span>
                            ) : (
                              <img
                                src={imageURL}
                                alt="Profile preview"
                                className="h-full w-full object-cover"
                                onError={handleImageError}
                              />
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[13px] font-medium text-foreground">
                              {imageLoadFailed
                                ? 'Saved image unavailable'
                                : previewUnavailable
                                  ? 'No image selected'
                                  : 'Thumbnail ready'}
                            </p>
                            <p className="truncate text-[12px] text-muted-foreground">
                              JPG or PNG, square image recommended.
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-2 sm:shrink-0">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleOpenImagePicker}
                            className="flex-1 sm:flex-none"
                          >
                            <ImagePlus className="mr-2 h-4 w-4" />
                            {previewUnavailable ? 'Upload' : 'Change'}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleRemoveImage}
                            disabled={!imageURL && !formData.profile_image}
                            className="flex-1 sm:flex-none"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end pt-2">
                      <Button type="submit" disabled={isSaving}>
                        <Save className="mr-2 h-4 w-4" />
                        {isSaving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}
          </AppPageSection>
        </div>

        <Dialog
          open={feedbackDialog.open}
          onOpenChange={(open) =>
            setFeedbackDialog((current) => ({
              ...current,
              open,
            }))
          }
        >
          <DialogContent
            className={cn(
              'max-w-md gap-0 overflow-hidden rounded-2xl bg-background p-0 shadow-2xl',
              feedbackDialog.tone === 'success'
                ? 'border border-emerald-500/20'
                : 'border border-rose-500/20'
            )}
          >
            <DialogHeader
              className={cn(
                'border-b border-border/60 px-5 py-4',
                feedbackDialog.tone === 'success'
                  ? 'bg-emerald-500/6'
                  : 'bg-rose-500/6'
              )}
            >
              <div className="flex items-start gap-3">
                <span
                  className={cn(
                    'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                    feedbackDialog.tone === 'success'
                      ? 'bg-emerald-500/12 text-emerald-600 dark:text-emerald-300'
                      : 'bg-rose-500/12 text-rose-600 dark:text-rose-300'
                  )}
                >
                  {feedbackDialog.tone === 'success' ? (
                    <CheckCircle2 className="h-[18px] w-[18px]" />
                  ) : (
                    <AlertTriangle className="h-[18px] w-[18px]" />
                  )}
                </span>
                <div className="space-y-1">
                  <DialogTitle className="text-base font-semibold text-foreground">
                    {feedbackDialog.title}
                  </DialogTitle>
                  <p className="text-[13px] leading-5 text-muted-foreground">
                    {feedbackDialog.tone === 'success'
                      ? 'Your profile update completed successfully.'
                      : 'The profile update could not be completed.'}
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-4 px-5 py-5">
              <div
                className={cn(
                  'rounded-xl px-4 py-3',
                  feedbackDialog.tone === 'success'
                    ? 'border border-emerald-500/15 bg-emerald-500/6'
                    : 'border border-rose-500/15 bg-rose-500/6'
                )}
              >
                <p className="whitespace-pre-wrap break-words text-[13px] leading-6 text-foreground">
                  {feedbackDialog.message}
                </p>
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={() =>
                    setFeedbackDialog((current) => ({
                      ...current,
                      open: false,
                    }))
                  }
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ErrorBoundary>
  );
};

export default Account;
